import { $streemApiV1 } from "shared/boot/api";
import { v1BaseUrl } from "shared/constants";
import { currentToken } from "shared/helpers/authorization";
import { randomUUID } from "shared/helpers/uuid";
import type { Nullable } from "shared/types";

// Buffers Segment track/pageview events and relays them, batched, to the
// first-party backend endpoint (POST /v1/analytics/events). The browser Segment
// SDK is blocked for ad-blocker users; this first-party path is not. See
// `packages/shared/boot/analytics.js` for the wiring and the SegmentProvider
// relay in `@streemau/analytics-proxy`.

const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 10_000;

export interface RelayItem {
  event?: string;
  name?: string;
  properties?: Record<string, unknown>;
  type: "page" | "track";
}

interface RelayPayload extends RelayItem {
  context: Record<string, unknown>;
  message_id: string;
  timestamp: string;
}

let buffer: RelayPayload[] = [];
let intervalId: Nullable<ReturnType<typeof setInterval>> = null;
let cachedToken: Nullable<string> = null;
let isListening = false;
// message_ids that have already failed a flush once, so we retry each at most once.
const failedOnce = new Set<string>();

const UTM_TO_CAMPAIGN: Record<string, string> = {
  utm_campaign: "name",
  utm_content: "content",
  utm_medium: "medium",
  utm_source: "source",
  utm_term: "term",
};

interface UserAgentDataBrand {
  brand: string;
  version: string;
}

interface UserAgentData {
  brands: UserAgentDataBrand[];
  mobile: boolean;
  platform: string;
}

// Chromium-only, not yet in TS's lib.dom.d.ts.
interface NavigatorWithUserAgentData extends Navigator {
  userAgentData?: UserAgentData;
}

function parseCampaign(search: string): Nullable<Record<string, string>> {
  const params = new URLSearchParams(search);
  const campaign: Record<string, string> = {};

  Object.entries(UTM_TO_CAMPAIGN).forEach(([utm, key]) => {
    const value = params.get(utm);

    if (value) campaign[key] = value;
  });

  return Object.keys(campaign).length > 0 ? campaign : null;
}

// Mirrors the context the browser Segment SDK adds automatically, which is lost
// when relaying server-side.
function captureContext(): Record<string, unknown> {
  const { href, pathname, search } = window.location;
  const campaign = parseCampaign(search);
  const { userAgentData } = navigator as NavigatorWithUserAgentData;

  return {
    ...(campaign && { campaign }),
    locale: navigator.language,
    page: {
      path: pathname,
      referrer: document.referrer,
      search,
      title: document.title,
      url: href,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
    ...(userAgentData && { userAgentData }),
  };
}

async function refreshCachedToken(): Promise<void> {
  try {
    cachedToken = (await currentToken()) || null;
  } catch {
    // Best-effort: keep the previous token for the unload path.
  }
}

function requeueOnce(events: RelayPayload[]): void {
  const retryable = events.filter((event) => {
    if (failedOnce.has(event.message_id)) {
      failedOnce.delete(event.message_id);

      return false;
    }

    failedOnce.add(event.message_id);

    return true;
  });

  buffer.unshift(...retryable);
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return;

  const events = buffer.splice(0, MAX_BATCH);

  try {
    await $streemApiV1.post("analytics/events", { params: { events } });
    events.forEach((event) => failedOnce.delete(event.message_id));
    refreshCachedToken().catch(() => {});
  } catch {
    requeueOnce(events);
  }
}

// Flush on page hide via fetch keepalive (sendBeacon can't set the bearer
// Authorization header). Fire-and-forget, no retry: the page is going away.
function flushWithKeepalive(): void {
  if (buffer.length === 0 || !cachedToken) return;

  while (buffer.length > 0) {
    const events = buffer.splice(0, MAX_BATCH);

    fetch(`${v1BaseUrl}/analytics/events`, {
      body: JSON.stringify({ events }),
      headers: {
        Authorization: `Bearer ${cachedToken}`,
        "Content-Type": "application/json",
      },
      keepalive: true,
      method: "POST",
    }).catch(() => {});
  }
}

function handleVisibilityChange(): void {
  if (document.visibilityState === "hidden") flushWithKeepalive();
}

export function enqueue(item: RelayItem): void {
  buffer.push({
    ...item,
    context: captureContext(),
    message_id: randomUUID(),
    timestamp: new Date().toISOString(),
  });

  if (buffer.length >= MAX_BATCH) flush().catch(() => {});
}

export function start(): void {
  if (isListening) return;

  isListening = true;
  refreshCachedToken().catch(() => {});

  intervalId = setInterval(() => {
    flush().catch(() => {});
  }, FLUSH_INTERVAL_MS);

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", flushWithKeepalive);
}

export function stop(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }

  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("pagehide", flushWithKeepalive);
  isListening = false;
  buffer = [];
  failedOnce.clear();
}
